/**
 * Relationship Compass - Communication Style Assessment
 *
 * This assessment helps couples understand their individual communication patterns
 * and how their styles interact, complement, or clash. It uses scenario-based
 * questions to reveal actual communication behaviours, not ideal responses.
 *
 * Design Philosophy:
 * - Scenario-based to reveal real behaviour, not aspirational answers
 * - Non-judgmental—all communication styles have strengths and challenges
 * - Reveals couple dynamics, not just individual patterns
 * - Actionable insights linked to specific communication modules
 * - DDD brand voice: warm, research-grounded, aspirational but realistic
 * - Australian English spelling throughout
 */

const CommunicationStyleProfile = {
  id: 'CSP-1',
  title: "Communication Style Profile",
  subtitle: "Understand how you and your partner communicate, and how your styles interact",
  duration: "15-20 minutes",
  questionCount: 20, // 4 questions per area

  introduction: {
    heading: "Discovering Your Communication Patterns",
    content: `The way you communicate shapes every aspect of your relationship. But most of us have never stopped to understand *how* we actually communicate. We just do it.

This assessment helps you see your communication patterns clearly: how you handle conflict, express emotions, listen, receive feedback, and process information.

**How it works:**
- You'll respond to realistic scenarios based on what you'd actually do, not what you think you should do
- Your partner can complete this separately, then you'll see how your styles interact
- There are no "better" or "worse" styles, only patterns to understand
- Results will show where your styles complement each other and where they clash

Answer honestly. The goal isn't to look good; it's to see clearly.

Ready?`,
  },

  areas: {
    conflictStyle: {
      name: "Conflict Communication Style",
      description: "How you engage during disagreements and tension",
      icon: "⚡",
      color: "red",
      module: 4, // Links to Module 4: Conflict & Repair
      dimensions: ['pursuerWithdrawer', 'escalationPattern', 'repairApproach'],
      questions: [0, 1, 2, 3]
    },

    emotionalExpression: {
      name: "Emotional Expression Style",
      description: "How you share feelings and navigate vulnerability",
      icon: "❤️",
      color: "rose",
      module: 5, // Links to Module 5: Intimacy & Vulnerability
      dimensions: ['vulnerabilityComfort', 'expressivenessLevel', 'emotionalDirectness'],
      questions: [4, 5, 6, 7]
    },

    listeningStyle: {
      name: "Listening Style",
      description: "How you take in and respond to what your partner shares",
      icon: "👂",
      color: "blue",
      module: 3, // Links to Module 3: Communication Patterns
      dimensions: ['listeningMode', 'presenceLevel', 'responsePattern'],
      questions: [8, 9, 10, 11]
    },

    feedbackReception: {
      name: "Feedback Reception",
      description: "How you respond when receiving criticism or feedback",
      icon: "💭",
      color: "purple",
      module: 4, // Links to Module 4: Conflict & Repair
      dimensions: ['defensiveness', 'growthOrientation', 'feedbackProcessing'],
      questions: [12, 13, 14, 15]
    },

    informationProcessing: {
      name: "Information Processing",
      description: "How you think through and respond to important conversations",
      icon: "🧠",
      color: "teal",
      module: 3, // Links to Module 3: Communication Patterns
      dimensions: ['processingSpeed', 'verbalWritten', 'responseTimeline'],
      questions: [16, 17, 18, 19]
    }
  },

  questions: [
    // CONFLICT COMMUNICATION STYLE (Questions 0-3)
    {
      id: 0,
      area: 'conflictStyle',
      type: "scenario",
      text: "Your partner seems upset and distant after you made plans without checking in first. What's your most likely response?",
      options: [
        {
          text: "Bring it up directly: 'You seem upset. Can we talk about what's going on?'",
          weights: {
            pursuerWithdrawer: 2, // Pursuer
            escalationPattern: 0,
            repairApproach: 2
          }
        },
        {
          text: "Wait for them to bring it up. I don't want to make it worse by pushing",
          weights: {
            pursuerWithdrawer: -2, // Withdrawer
            escalationPattern: -1,
            repairApproach: -1
          }
        },
        {
          text: "Give them space but signal I'm available: 'I'm here when you're ready to talk'",
          weights: {
            pursuerWithdrawer: 0,
            escalationPattern: -1,
            repairApproach: 1
          }
        },
        {
          text: "Feel anxious and keep checking in: 'Are you okay? Are we okay? What's wrong?'",
          weights: {
            pursuerWithdrawer: 2,
            escalationPattern: 1,
            repairApproach: 0
          }
        }
      ]
    },

    {
      id: 1,
      area: 'conflictStyle',
      type: "scenario",
      text: "During a disagreement, your partner raises their voice. You're most likely to:",
      options: [
        {
          text: "Raise my voice too. If they're going to escalate, so will I",
          weights: {
            pursuerWithdrawer: 1,
            escalationPattern: 2, // Escalator
            repairApproach: -2
          }
        },
        {
          text: "Go quiet and withdraw; I shut down when volume increases",
          weights: {
            pursuerWithdrawer: -2,
            escalationPattern: -2, // De-escalator
            repairApproach: -1
          }
        },
        {
          text: "Name it calmly: 'I notice we're both getting louder. Can we pause?'",
          weights: {
            pursuerWithdrawer: 0,
            escalationPattern: -1,
            repairApproach: 2
          }
        },
        {
          text: "Match their energy but stay focussed on the issue at hand",
          weights: {
            pursuerWithdrawer: 1,
            escalationPattern: 1,
            repairApproach: 0
          }
        }
      ]
    },

    {
      id: 2,
      area: 'conflictStyle',
      type: "scenario",
      text: "After a significant argument, what's your typical pattern for reconnecting?",
      options: [
        {
          text: "I need to talk it through fully before I can reconnect: resolution first, then affection",
          weights: {
            pursuerWithdrawer: 2,
            escalationPattern: 0,
            repairApproach: 1
          }
        },
        {
          text: "I need space first, then I can talk. Pushing me to reconnect too soon backfires",
          weights: {
            pursuerWithdrawer: -2,
            escalationPattern: -1,
            repairApproach: -1
          }
        },
        {
          text: "A small gesture (touch, humour, acknowledgement) helps me soften and reengage",
          weights: {
            pursuerWithdrawer: 0,
            escalationPattern: -1,
            repairApproach: 2
          }
        },
        {
          text: "I tend to move on quickly and don't always revisit what happened",
          weights: {
            pursuerWithdrawer: -1,
            escalationPattern: 0,
            repairApproach: -2
          }
        }
      ]
    },

    {
      id: 3,
      area: 'conflictStyle',
      type: "scenario",
      text: "Your partner wants to have 'a serious talk about the relationship.' Your immediate internal reaction?",
      options: [
        {
          text: "Relief: 'Finally, we're going to address what's been bothering me'",
          weights: {
            pursuerWithdrawer: 2,
            escalationPattern: 0,
            repairApproach: 1
          }
        },
        {
          text: "Anxiety: 'Oh no, what did I do wrong? Are we breaking up?'",
          weights: {
            pursuerWithdrawer: 1,
            escalationPattern: 1,
            repairApproach: -1
          }
        },
        {
          text: "Dread: 'Here we go again. I just want things to be easy'",
          weights: {
            pursuerWithdrawer: -2,
            escalationPattern: 0,
            repairApproach: -2
          }
        },
        {
          text: "Curiosity: 'Okay, let's figure out what's on their mind'",
          weights: {
            pursuerWithdrawer: 0,
            escalationPattern: -1,
            repairApproach: 2
          }
        }
      ]
    },

    // EMOTIONAL EXPRESSION STYLE (Questions 4-7)
    {
      id: 4,
      area: 'emotionalExpression',
      type: "scenario",
      text: "Something your partner did really hurt your feelings. How do you typically handle it?",
      options: [
        {
          text: "Tell them directly and clearly: 'When you did X, I felt hurt because Y'",
          weights: {
            vulnerabilityComfort: 2,
            expressivenessLevel: 2,
            emotionalDirectness: 2
          }
        },
        {
          text: "Drop hints or act differently, hoping they'll notice and ask what's wrong",
          weights: {
            vulnerabilityComfort: -1,
            expressivenessLevel: 0,
            emotionalDirectness: -2
          }
        },
        {
          text: "Minimise it: 'It's fine, I'm probably overreacting anyway'",
          weights: {
            vulnerabilityComfort: -2,
            expressivenessLevel: -2,
            emotionalDirectness: -1
          }
        },
        {
          text: "Wait until I've processed it, then bring it up when I'm calmer",
          weights: {
            vulnerabilityComfort: 1,
            expressivenessLevel: 0,
            emotionalDirectness: 1
          }
        }
      ]
    },

    {
      id: 5,
      area: 'emotionalExpression',
      type: "scenario",
      text: "Your partner asks, 'How are you feeling about us lately?' You're most likely to say:",
      options: [
        {
          text: "Share openly and vulnerably about what's actually going on for me",
          weights: {
            vulnerabilityComfort: 2,
            expressivenessLevel: 2,
            emotionalDirectness: 2
          }
        },
        {
          text: "'Fine' or 'Good', even if that's not the whole truth",
          weights: {
            vulnerabilityComfort: -2,
            expressivenessLevel: -2,
            emotionalDirectness: -2
          }
        },
        {
          text: "Share surface-level feelings but avoid the deeper, scarier stuff",
          weights: {
            vulnerabilityComfort: -1,
            expressivenessLevel: 0,
            emotionalDirectness: 0
          }
        },
        {
          text: "Deflect with humour or change the subject to avoid heaviness",
          weights: {
            vulnerabilityComfort: -1,
            expressivenessLevel: -1,
            emotionalDirectness: -2
          }
        }
      ]
    },

    {
      id: 6,
      area: 'emotionalExpression',
      type: "scenario",
      text: "When you're scared or anxious about something in the relationship, you:",
      options: [
        {
          text: "Share it with my partner directly; vulnerability brings us closer",
          weights: {
            vulnerabilityComfort: 2,
            expressivenessLevel: 2,
            emotionalDirectness: 2
          }
        },
        {
          text: "Talk to a friend first, then maybe my partner if it still feels relevant",
          weights: {
            vulnerabilityComfort: -1,
            expressivenessLevel: 0,
            emotionalDirectness: -1
          }
        },
        {
          text: "Keep it to myself. I don't want to burden them or create unnecessary worry",
          weights: {
            vulnerabilityComfort: -2,
            expressivenessLevel: -2,
            emotionalDirectness: -2
          }
        },
        {
          text: "Eventually share, but only after I've worked through it on my own first",
          weights: {
            vulnerabilityComfort: 0,
            expressivenessLevel: -1,
            emotionalDirectness: 0
          }
        }
      ]
    },

    {
      id: 7,
      area: 'emotionalExpression',
      type: "scenario",
      text: "Your preferred way of expressing love and affection is:",
      options: [
        {
          text: "Words: I say 'I love you' and express feelings verbally often",
          weights: {
            vulnerabilityComfort: 1,
            expressivenessLevel: 2,
            emotionalDirectness: 2
          }
        },
        {
          text: "Actions: I show love through what I do, not what I say",
          weights: {
            vulnerabilityComfort: 0,
            expressivenessLevel: -1,
            emotionalDirectness: -1
          }
        },
        {
          text: "Physical touch: hugs, hand-holding, closeness speak louder than words",
          weights: {
            vulnerabilityComfort: 1,
            expressivenessLevel: 1,
            emotionalDirectness: 0
          }
        },
        {
          text: "I assume they know; I don't feel the need to express it constantly",
          weights: {
            vulnerabilityComfort: -1,
            expressivenessLevel: -2,
            emotionalDirectness: -2
          }
        }
      ]
    },

    // LISTENING STYLE (Questions 8-11)
    {
      id: 8,
      area: 'listeningStyle',
      type: "scenario",
      text: "Your partner is venting about a tough day at work. You're most likely to:",
      options: [
        {
          text: "Jump in with solutions: 'Have you tried...?' or 'What if you...'",
          weights: {
            listeningMode: -2, // Fix-it listening
            presenceLevel: -1,
            responsePattern: 2
          }
        },
        {
          text: "Listen empathetically: 'That sounds really hard. I'm sorry you're dealing with that'",
          weights: {
            listeningMode: 2, // Empathetic listening
            presenceLevel: 2,
            responsePattern: -1
          }
        },
        {
          text: "Listen but get distracted or check my phone while they talk",
          weights: {
            listeningMode: 0,
            presenceLevel: -2,
            responsePattern: 0
          }
        },
        {
          text: "Share a similar experience from my own life to show I understand",
          weights: {
            listeningMode: -1,
            presenceLevel: 0,
            responsePattern: 1
          }
        }
      ]
    },

    {
      id: 9,
      area: 'listeningStyle',
      type: "scenario",
      text: "When your partner is sharing something important, you:",
      options: [
        {
          text: "Give them my full attention: no phone, no distractions, eye contact",
          weights: {
            listeningMode: 2,
            presenceLevel: 2,
            responsePattern: 1
          }
        },
        {
          text: "Listen whilst doing something else; I can multitask",
          weights: {
            listeningMode: -1,
            presenceLevel: -2,
            responsePattern: -1
          }
        },
        {
          text: "Interrupt with questions or comments as they come to mind",
          weights: {
            listeningMode: -2,
            presenceLevel: 0,
            responsePattern: 2
          }
        },
        {
          text: "Let them finish completely before I respond",
          weights: {
            listeningMode: 2,
            presenceLevel: 2,
            responsePattern: -2
          }
        }
      ]
    },

    {
      id: 10,
      area: 'listeningStyle',
      type: "scenario",
      text: "After your partner shares something emotional, your typical response is:",
      options: [
        {
          text: "Reflect back what I heard: 'So it sounds like you're feeling...'",
          weights: {
            listeningMode: 2,
            presenceLevel: 2,
            responsePattern: -1
          }
        },
        {
          text: "Offer reassurance: 'It'll be okay' or 'Don't worry about it'",
          weights: {
            listeningMode: -1,
            presenceLevel: 0,
            responsePattern: 1
          }
        },
        {
          text: "Ask questions to better understand their experience",
          weights: {
            listeningMode: 1,
            presenceLevel: 1,
            responsePattern: 0
          }
        },
        {
          text: "Not sure how to respond, so I might change the subject or freeze up",
          weights: {
            listeningMode: -2,
            presenceLevel: -1,
            responsePattern: -2
          }
        }
      ]
    },

    {
      id: 11,
      area: 'listeningStyle',
      type: "scenario",
      text: "When listening to your partner, you notice yourself:",
      options: [
        {
          text: "Planning my response whilst they're still talking",
          weights: {
            listeningMode: -2,
            presenceLevel: -2,
            responsePattern: 2
          }
        },
        {
          text: "Truly present and curious about understanding their experience",
          weights: {
            listeningMode: 2,
            presenceLevel: 2,
            responsePattern: -1
          }
        },
        {
          text: "Getting defensive if what they're saying involves me",
          weights: {
            listeningMode: -2,
            presenceLevel: -1,
            responsePattern: 2
          }
        },
        {
          text: "Noticing their emotions and non-verbal cues, not just their words",
          weights: {
            listeningMode: 2,
            presenceLevel: 2,
            responsePattern: 0
          }
        }
      ]
    },

    // FEEDBACK RECEPTION (Questions 12-15)
    {
      id: 12,
      area: 'feedbackReception',
      type: "scenario",
      text: "Your partner says, 'Can I share something that's been bothering me?' Your immediate internal reaction:",
      options: [
        {
          text: "Defensiveness: 'Here we go, what did I do wrong now?'",
          weights: {
            defensiveness: 2,
            growthOrientation: -2,
            feedbackProcessing: -1
          }
        },
        {
          text: "Openness: 'Absolutely, I want to hear this'",
          weights: {
            defensiveness: -2,
            growthOrientation: 2,
            feedbackProcessing: 1
          }
        },
        {
          text: "Anxiety: 'Oh no, they're upset with me'",
          weights: {
            defensiveness: 1,
            growthOrientation: 0,
            feedbackProcessing: 0
          }
        },
        {
          text: "Curiosity: 'Okay, let's see what this is about'",
          weights: {
            defensiveness: -1,
            growthOrientation: 2,
            feedbackProcessing: 2
          }
        }
      ]
    },

    {
      id: 13,
      area: 'feedbackReception',
      type: "scenario",
      text: "Your partner shares that something you did hurt their feelings. You:",
      options: [
        {
          text: "Immediately explain why I did it and my intentions behind it",
          weights: {
            defensiveness: 2,
            growthOrientation: -1,
            feedbackProcessing: 1
          }
        },
        {
          text: "Listen first, acknowledge their hurt, then discuss my perspective",
          weights: {
            defensiveness: -2,
            growthOrientation: 2,
            feedbackProcessing: 2
          }
        },
        {
          text: "Point out things they've done that hurt me too",
          weights: {
            defensiveness: 2,
            growthOrientation: -2,
            feedbackProcessing: -2
          }
        },
        {
          text: "Apologise immediately and genuinely, then work to understand what happened",
          weights: {
            defensiveness: -2,
            growthOrientation: 2,
            feedbackProcessing: 1
          }
        }
      ]
    },

    {
      id: 14,
      area: 'feedbackReception',
      type: "scenario",
      text: "When receiving criticism from your partner, you tend to:",
      options: [
        {
          text: "Feel attacked and defend myself vigorously",
          weights: {
            defensiveness: 2,
            growthOrientation: -2,
            feedbackProcessing: -2
          }
        },
        {
          text: "Shut down emotionally and go quiet",
          weights: {
            defensiveness: 1,
            growthOrientation: -1,
            feedbackProcessing: -1
          }
        },
        {
          text: "Ask clarifying questions to understand their perspective",
          weights: {
            defensiveness: -2,
            growthOrientation: 2,
            feedbackProcessing: 2
          }
        },
        {
          text: "Take it in, even if it stings, and reflect on whether it's valid",
          weights: {
            defensiveness: -2,
            growthOrientation: 2,
            feedbackProcessing: 2
          }
        }
      ]
    },

    {
      id: 15,
      area: 'feedbackReception',
      type: "scenario",
      text: "After receiving difficult feedback from your partner, you typically:",
      options: [
        {
          text: "Ruminate on it for days, feeling hurt or angry",
          weights: {
            defensiveness: 1,
            growthOrientation: -1,
            feedbackProcessing: -1
          }
        },
        {
          text: "Think about it, then come back later with a more thoughtful response",
          weights: {
            defensiveness: -1,
            growthOrientation: 2,
            feedbackProcessing: 2
          }
        },
        {
          text: "Dismiss it; they're being too sensitive or unreasonable",
          weights: {
            defensiveness: 2,
            growthOrientation: -2,
            feedbackProcessing: -2
          }
        },
        {
          text: "Use it as an opportunity to understand myself and grow",
          weights: {
            defensiveness: -2,
            growthOrientation: 2,
            feedbackProcessing: 2
          }
        }
      ]
    },

    // INFORMATION PROCESSING (Questions 16-19)
    {
      id: 16,
      area: 'informationProcessing',
      type: "scenario",
      text: "Your partner brings up a big decision that needs discussion. You prefer to:",
      options: [
        {
          text: "Talk it through right now. I process out loud and think best in conversation",
          weights: {
            processingSpeed: 2,
            verbalWritten: 2,
            responseTimeline: 2
          }
        },
        {
          text: "Think about it first, then discuss later. I need time to process internally",
          weights: {
            processingSpeed: -2,
            verbalWritten: -1,
            responseTimeline: -2
          }
        },
        {
          text: "Write down my thoughts before we talk; organising on paper helps me clarify",
          weights: {
            processingSpeed: -1,
            verbalWritten: -2,
            responseTimeline: -1
          }
        },
        {
          text: "Discuss it now but revisit later after I've had time to think",
          weights: {
            processingSpeed: 0,
            verbalWritten: 1,
            responseTimeline: 0
          }
        }
      ]
    },

    {
      id: 17,
      area: 'informationProcessing',
      type: "scenario",
      text: "During an important conversation, you:",
      options: [
        {
          text: "Respond quickly; my thoughts and words come fast",
          weights: {
            processingSpeed: 2,
            verbalWritten: 2,
            responseTimeline: 2
          }
        },
        {
          text: "Take pauses to gather my thoughts before responding",
          weights: {
            processingSpeed: -1,
            verbalWritten: 0,
            responseTimeline: -1
          }
        },
        {
          text: "Struggle to articulate in the moment: 'I need time to think about this'",
          weights: {
            processingSpeed: -2,
            verbalWritten: -2,
            responseTimeline: -2
          }
        },
        {
          text: "Think out loud, even if my thoughts aren't fully formed yet",
          weights: {
            processingSpeed: 2,
            verbalWritten: 2,
            responseTimeline: 1
          }
        }
      ]
    },

    {
      id: 18,
      area: 'informationProcessing',
      type: "scenario",
      text: "When you need to communicate something important to your partner, you're more likely to:",
      options: [
        {
          text: "Say it in person. Talking face-to-face works best for me",
          weights: {
            processingSpeed: 1,
            verbalWritten: 2,
            responseTimeline: 1
          }
        },
        {
          text: "Write it out (text, email, letter). I express myself better in writing",
          weights: {
            processingSpeed: -1,
            verbalWritten: -2,
            responseTimeline: -1
          }
        },
        {
          text: "Bring it up spontaneously when it comes to mind",
          weights: {
            processingSpeed: 2,
            verbalWritten: 1,
            responseTimeline: 2
          }
        },
        {
          text: "Plan what I want to say beforehand, then schedule a time to talk",
          weights: {
            processingSpeed: -2,
            verbalWritten: 0,
            responseTimeline: -2
          }
        }
      ]
    },

    {
      id: 19,
      area: 'informationProcessing',
      type: "scenario",
      text: "After a difficult conversation, you:",
      options: [
        {
          text: "Feel complete. Once we've talked, I'm done processing",
          weights: {
            processingSpeed: 2,
            verbalWritten: 2,
            responseTimeline: 2
          }
        },
        {
          text: "Need time to digest what was said before I know how I feel",
          weights: {
            processingSpeed: -2,
            verbalWritten: -1,
            responseTimeline: -2
          }
        },
        {
          text: "Often think of things I wish I'd said and want to revisit",
          weights: {
            processingSpeed: -1,
            verbalWritten: -1,
            responseTimeline: -1
          }
        },
        {
          text: "Process by writing about it in a journal or talking to someone else",
          weights: {
            processingSpeed: -1,
            verbalWritten: -2,
            responseTimeline: -1
          }
        }
      ]
    }
  ],

  // Calculate scores from weighted responses
  calculateScores: (responses) => {
    const dimensionScores = {};
    const dimensionCounts = {};

    // Initialize all dimensions across all areas
    Object.values(CommunicationStyleProfile.areas).forEach(area => {
      area.dimensions.forEach(dim => {
        dimensionScores[dim] = 0;
        dimensionCounts[dim] = 0;
      });
    });

    // Aggregate weighted scores
    responses.forEach(response => {
      const question = CommunicationStyleProfile.questions[response.questionId];
      const option = question.options[response.selectedIndex];

      if (option.weights) {
        Object.entries(option.weights).forEach(([dimension, weight]) => {
          if (dimensionScores[dimension] !== undefined) {
            dimensionScores[dimension] += weight;
            dimensionCounts[dimension]++;
          }
        });
      }
    });

    // Normalise to 0-100 scale
    const normalisedScores = {};
    Object.keys(dimensionScores).forEach(dim => {
      if (dimensionCounts[dim] > 0) {
        const avgScore = dimensionScores[dim] / dimensionCounts[dim];
        // Convert from -2 to +2 range to 0-100
        normalisedScores[dim] = Math.round(((avgScore + 2) / 4) * 100);
      } else {
        normalisedScores[dim] = 50;
      }
    });

    return normalisedScores;
  },

  // Determine communication profile type
  getProfileType: (scores) => {
    const pursuerWithdrawer = scores.pursuerWithdrawer || 50;
    const vulnerabilityComfort = scores.vulnerabilityComfort || 50;
    const listeningMode = scores.listeningMode || 50;
    const defensiveness = scores.defensiveness || 50;

    // High pursuer (>65) + High vulnerability (>65) = Emotionally Expressive Pursuer
    if (pursuerWithdrawer >= 65 && vulnerabilityComfort >= 65) {
      return 'expressivePursuer';
    }

    // Low pursuer (<40) + Low vulnerability (<40) = Conflict Avoider
    if (pursuerWithdrawer <= 40 && vulnerabilityComfort <= 40) {
      return 'conflictAvoider';
    }

    // High defensiveness (>65) = Defensive Communicator
    if (defensiveness >= 65) {
      return 'defensiveCommunicator';
    }

    // High listening (>70) + Low defensiveness (<35) = Attuned Listener
    if (listeningMode >= 70 && defensiveness <= 35) {
      return 'attunedListener';
    }

    // High pursuer + High defensiveness = Anxious Pursuer
    if (pursuerWithdrawer >= 60 && defensiveness >= 60) {
      return 'anxiousPursuer';
    }

    // Balanced scores = Balanced Communicator
    const avgScore = Object.values(scores).reduce((a, b) => a + b, 0) / Object.keys(scores).length;
    if (avgScore >= 45 && avgScore <= 60) {
      return 'balancedCommunicator';
    }

    // Default
    return 'developingCommunicator';
  },

  profileTypes: {
    expressivePursuer: {
      name: "The Emotionally Expressive Pursuer",
      tagline: "You seek connection through dialogue and aren't afraid to feel",
      description: "You naturally move toward your partner during conflict, seeking resolution through conversation. You're comfortable with vulnerability and emotional expression. You value talking things through and can get anxious when issues remain unaddressed. Your strength is emotional courage; your challenge is giving space when your partner needs it.",
      strengths: [
        "Comfortable with vulnerability and emotional expression",
        "Values dialogue and resolving issues directly",
        "Brings emotional honesty to the relationship",
        "Not afraid of difficult conversations"
      ],
      challenges: [
        "Can overwhelm partners who need processing time",
        "May pursue too aggressively during conflict",
        "Anxiety when issues aren't immediately addressed",
        "Difficulty giving space without feeling rejected"
      ],
      compatibilityInsights: {
        withSameType: "Two expressive pursuers can have intense, emotionally rich communication, but may escalate quickly without space to cool down.",
        withConflictAvoider: "Classic pursuer-withdrawer dynamic. Pursuer's intensity triggers withdrawer's shutdown, which intensifies pursuer's anxiety. Requires conscious pattern interruption.",
        withAttunedListener: "Excellent match. Listener can handle pursuer's emotional intensity without defensiveness. Pursuer feels heard and valued."
      }
    },

    conflictAvoider: {
      name: "The Conflict Avoider",
      tagline: "You value harmony and process internally before engaging",
      description: "You tend to withdraw during conflict, preferring to process internally before discussing issues. You value peace and may minimise problems to avoid tension. You're more comfortable showing love through actions than words. Your strength is calm reflection; your challenge is speaking up before resentment builds.",
      strengths: [
        "Thoughtful and reflective in processing",
        "Rarely escalates conflict unnecessarily",
        "Values stability and peace",
        "Can de-escalate tense situations"
      ],
      challenges: [
        "May avoid necessary conversations too long",
        "Partner can feel shut out or rejected",
        "Resentment builds when needs go unexpressed",
        "Difficulty with emotional vulnerability"
      ],
      compatibilityInsights: {
        withExpressivePursuer: "Classic pursuer-withdrawer dynamic. Your withdrawal triggers their pursuit, creating a painful cycle. Both need to meet in the middle.",
        withSameType: "Two avoiders can have a peaceful relationship, but important issues may never get addressed, leading to slow erosion of intimacy.",
        withBalancedCommunicator: "Good match if balanced partner can gently encourage more openness without pushing too hard."
      }
    },

    defensiveCommunicator: {
      name: "The Defensive Communicator",
      tagline: "You protect yourself quickly when feeling criticised",
      description: "You respond to feedback with defensiveness, often explaining, justifying, or counter-attacking. This protective pattern likely developed from past experiences where you felt unfairly blamed. Your challenge is developing the safety and skills to hear feedback without reflexive self-protection.",
      strengths: [
        "Self-aware enough to recognise the pattern",
        "Often articulate in explaining your perspective",
        "Protective of your own needs and boundaries",
        "Motivated to improve communication"
      ],
      challenges: [
        "Reflexive defensiveness blocks genuine dialogue",
        "Partner feels unheard and may stop sharing",
        "Feedback becomes battleground rather than growth opportunity",
        "Difficulty taking responsibility"
      ],
      compatibilityInsights: {
        withAttunedListener: "Listener's non-reactive presence can help you feel safer and lower defences over time.",
        withExpressivePursuer: "High conflict potential. Pursuer's intensity triggers your defences, which triggers their pursuit: a painful cycle.",
        withSameType: "Two defensive communicators struggle to hear each other. Every conversation becomes about who's right."
      }
    },

    attunedListener: {
      name: "The Attuned Listener",
      tagline: "You listen deeply and respond with empathy and curiosity",
      description: "You excel at truly hearing your partner without defensiveness or problem-solving. You're curious about their experience and emotionally present during conversations. You rarely interrupt and can hold space for difficult emotions. Your strength is empathetic listening; your challenge may be ensuring your own needs get voiced.",
      strengths: [
        "Exceptional listening and empathy skills",
        "Non-defensive and open to feedback",
        "Creates safety for partner's vulnerability",
        "Emotionally mature and regulated"
      ],
      challenges: [
        "May neglect your own needs to keep peace",
        "Could over-function emotionally for the relationship",
        "Might not speak up about your own hurts",
        "Partner may take your listening for granted"
      ],
      compatibilityInsights: {
        withExpressivePursuer: "Excellent match. You can handle their emotional intensity without reactivity.",
        withConflictAvoider: "You create safety that can help avoider open up, but may need to actively encourage their voice.",
        withDefensiveCommunicator: "Your non-reactive presence can lower their defences over time."
      }
    },

    anxiousPursuer: {
      name: "The Anxious Pursuer",
      tagline: "You seek reassurance through conversation but defend when hurt",
      description: "You want connection and resolution but can become defensive when feeling criticised. You pursue dialogue to reduce anxiety but your reactivity can escalate conflict. This pattern often stems from insecure attachment. Your growth edge is developing self-soothing and non-defensive listening.",
      strengths: [
        "Values the relationship deeply",
        "Willing to engage in difficult conversations",
        "Emotionally expressive and honest",
        "Recognises when something feels off"
      ],
      challenges: [
        "Anxiety drives pursuit, which can overwhelm partner",
        "Defensiveness prevents hearing partner's perspective",
        "Can escalate conflicts unintentionally",
        "Difficulty self-soothing during disconnection"
      ],
      compatibilityInsights: {
        withAttunedListener: "Listener's calm presence can help soothe your anxiety and model non-defensiveness.",
        withConflictAvoider: "Painful dynamic. Your pursuit triggers their withdrawal, which intensifies your anxiety.",
        withSameType: "High intensity relationship. Can be deeply connected or highly volatile."
      }
    },

    balancedCommunicator: {
      name: "The Balanced Communicator",
      tagline: "You're developing skills across multiple communication dimensions",
      description: "You show moderate skill across communication areas without extreme patterns. You can pursue when needed and give space when appropriate. You're working on vulnerability and listening. Your communication isn't perfect, but it's functional and improving. Keep building on this foundation.",
      strengths: [
        "Flexibility in communication approach",
        "Awareness of growth areas",
        "No extreme patterns that derail connection",
        "Open to learning and development"
      ],
      challenges: [
        "May lack confidence in communication skills",
        "Could benefit from more intentional practice",
        "Patterns may shift under stress",
        "Opportunity for deeper skill development"
      ],
      compatibilityInsights: {
        withAnyType: "Your flexibility allows you to adapt to various communication styles. Focus on understanding your partner's patterns and adjusting accordingly."
      }
    },

    developingCommunicator: {
      name: "The Developing Communicator",
      tagline: "You're building communication skills from the ground up",
      description: "Your scores suggest you're at the beginning of your communication development journey. That's okay. Communication skills are learnable. Focus on one area at a time: start with listening, then vulnerability, then managing defensiveness. Small, consistent practice creates meaningful change.",
      strengths: [
        "Awareness that improvement is needed",
        "Taking the assessment shows commitment",
        "Fresh start to build healthy patterns",
        "Opportunity for significant growth"
      ],
      challenges: [
        "Multiple communication patterns need development",
        "May feel overwhelmed by where to start",
        "Past patterns may be deeply ingrained",
        "Requires patience and consistent practice"
      ],
      compatibilityInsights: {
        withAnyType: "Focus on your own development first. Work through the communication modules systematically. Consider professional support to accelerate your growth."
      }
    }
  },

  completionMessage: {
    heading: "Your Communication Style Profile is Ready",
    content: `We've mapped how you communicate across five key areas: conflict, emotional expression, listening, feedback reception, and information processing.

This isn't about labelling you. It's about understanding your patterns so you can work with them intentionally.

Every communication style has strengths and challenges. The goal isn't to become someone else; it's to develop flexibility, awareness, and skill whilst staying authentically you.

Let's see what your patterns reveal...`
  }
};

export default CommunicationStyleProfile;
